/**
 * useAssistantAction — shared submit + envelope handling for the assistant
 * action router (design doc §6 + §10).
 *
 * Both the GlobalAssistantDock and the full-screen AssistantPane call
 * `assistantAct` and must handle the result `kind` identically:
 *   - 'action'        → caller shows summary toast/banner, then navigates to target.
 *   - 'answer'        → caller shows content inline.
 *   - 'clarification' → caller shows the prompt, keeps input open for a follow-up.
 *   - 'needs_owner'   → caller shows the reason + owner-approval note.
 *
 * This hook owns the network call, loading + error state, and (for 'action')
 * the navigation via routeForTarget. It returns the parsed result so the caller
 * can render the kind-specific UI. NEVER imports @smartbox/* (invariant 1).
 */

import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { assistantAct, type AssistantActContext, type AssistantActionResult } from '../../api/assistant';
import { ApiRequestError } from '../../api/client';
import { routeForTarget } from '../../lib/assistantRoutes';

interface UseAssistantActionResult {
  /** True while a round-trip is in flight. */
  loading: boolean;
  /** Last error message (null when none). */
  error: string | null;
  /**
   * Submit a message. Resolves with the parsed envelope (or null on error).
   * When the result is kind:'action' with a navigable target and `navigate` is
   * not false, the hook pushes the deep link before resolving.
   */
  submit: (message: string, context?: AssistantActContext, navigate?: boolean) => Promise<AssistantActionResult | null>;
  /** Clear the current error (e.g. when the user edits the field again). */
  clearError: () => void;
}

function messageFor(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

export function useAssistantAction(): UseAssistantActionResult {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const clearError = useCallback(() => setError(null), []);

  const submit = useCallback(
    async (
      message: string,
      context?: AssistantActContext,
      navigate = true,
    ): Promise<AssistantActionResult | null> => {
      const text = message.trim();
      if (!text || inFlight.current) return null;
      inFlight.current = true;
      setError(null);
      setLoading(true);
      try {
        const result = await assistantAct(text, context);
        if (navigate && result.kind === 'action' && result.target) {
          router.push(routeForTarget(result.target));
        }
        return result;
      } catch (err) {
        setError(messageFor(err));
        return null;
      } finally {
        inFlight.current = false;
        setLoading(false);
      }
    },
    [router],
  );

  return { loading, error, submit, clearError };
}
